/**
 * /customer/redemptions — My Redemptions list (Phase 2d).
 *
 * Mirror of mobile (customer)/redemptions.tsx (lines 32–189):
 *   - VAULT / My Redemptions header
 *   - Tabs: Active | Used | Expired (operator §4 default = Active)
 *   - Flat chronological list (operator §5 — no brand grouping)
 *   - Per-tab empty states with mobile-verbatim body copy
 *   - Row tap → /customer/redemption/:id detail
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Ticket, KeyRound, ChevronRight } from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'used', label: 'Used' },
  { key: 'expired', label: 'Expired' },
];

export default function CustomerRedemptionsPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const [tab, setTab] = useState('active'); // Operator §4 — default

  const listQ = useQuery({
    queryKey: qk.customerRedemptions(),
    queryFn: () => api.customerRedemptions(token),
    enabled: !!token,
    refetchOnWindowFocus: true,
  });
  const visible = useMemo(() => {
    return (listQ.data ?? []).filter((r) => r.status === tab);
  }, [listQ.data, tab]);

  return (
    <div className="space-y-6" data-testid="customer-redemptions-screen">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="customer-redemptions-back"
          className="w-9 h-9 rounded-full bg-[#15151B] border border-white/10 grid place-items-center text-white/75 hover:text-gold hover:border-gold/35 transition-colors"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-gold">
            Vault
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold leading-tight">
            My Redemptions
          </h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2" data-testid="customer-redemptions-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`customer-redemptions-tab-${t.key}`}
            aria-pressed={tab === t.key}
            className={
              'px-4 h-9 rounded-full text-xs font-semibold border transition-colors ' +
              (tab === t.key
                ? 'bg-gold/15 border-gold/45 text-gold'
                : 'bg-[#15151B] border-white/10 text-white/65 hover:text-white hover:border-white/20')
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Body */}
      {listQ.isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 rounded-2xl bg-white/[0.03] border border-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div
          className="rounded-3xl border border-white/8 bg-[#15151B] px-6 py-12 text-center"
          data-testid={`customer-redemptions-empty-${tab}`}
        >
          <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/25 grid place-items-center mx-auto mb-4">
            <Ticket size={24} className="text-gold" />
          </div>
          <div className="font-display text-base font-semibold">
            No {tab} redemptions
          </div>
          <div className="text-xs text-white/55 mt-1 max-w-sm mx-auto">
            {tab === 'active'
              ? 'Redeem a reward to see it here.'
              : `Your ${tab} redemptions will appear here.`}
          </div>
          <Link
            to="/customer/rewards"
            data-testid="customer-redemptions-empty-browse"
            className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 rounded-xl bg-gold/15 border border-gold/45 text-gold text-sm font-medium hover:bg-gold/20 transition-colors"
          >
            Browse rewards
          </Link>
        </div>
      ) : (
        <div className="space-y-2.5">
          {visible.map((r) => (
            <RedemptionRow key={r.id} r={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function RedemptionRow({ r }) {
  const tint = r.brand?.primary_color || '#C9A84C';
  return (
    <Link
      to={`/customer/redemption/${r.id}`}
      data-testid={`customer-redemption-row-${r.redemption_code}`}
      className="flex items-center gap-3 p-3.5 rounded-2xl bg-[#15151B] border border-white/8 hover:border-gold/35 hover:shadow-glow-gold-sm transition-all"
    >
      <div className="w-14 h-14 rounded-xl bg-[#1B1B22] border border-white/8 overflow-hidden shrink-0">
        {r.reward?.image_url ? (
          <img
            src={resolveImageUrl(r.reward.image_url)}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full grid place-items-center">
            <Ticket size={20} className="text-gold/70" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="text-[10px] uppercase tracking-[0.16em] font-mono"
          style={{ color: tint }}
        >
          {r.brand?.name ?? 'participating merchant'}
        </div>
        <div className="text-sm font-semibold mt-0.5 truncate">
          {r.reward?.title ?? 'Reward'}
        </div>
        <div className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-full bg-gold/10 border border-gold/30">
          <KeyRound size={10} className="text-gold" />
          <span className="text-[11px] font-bold tracking-[0.14em] text-gold">
            {r.redemption_code}
          </span>
        </div>
      </div>
      <ChevronRight size={18} className="text-white/35 shrink-0" />
    </Link>
  );
}
